export function BrowserFrame({
  className = '',
  children,
}: {
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className={`rounded-xl border bg-card shadow-2xl overflow-hidden ${className}`}>
      {/* Browser toolbar */}
      <div className="flex items-center gap-2 px-4 py-3 border-b bg-muted/30">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-500/80" />
          <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
          <div className="w-3 h-3 rounded-full bg-green-500/80" />
        </div>
        <div className="flex-1 mx-4">
          <div className="max-w-md mx-auto bg-background rounded-md px-3 py-1.5 text-xs text-muted-foreground flex items-center gap-2">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            app.positioniq.io/dashboard
          </div>
        </div>
      </div>
      {/* Browser content */}
      <div className="bg-background">
        {children}
      </div>
    </div>
  );
}

export function FlowConnector({ className = '' }: { className?: string }) {
  return (
    <svg
      className={`w-full h-4 md:h-6 ${className}`}
      viewBox="0 0 100 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="none"
    >
      <path
        d="M0 12H45L50 6L55 18L60 6L65 18L70 12H100"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray="4 4"
        className="text-primary/30"
      />
    </svg>
  );
}

export function StepConnectorLine({ className = '' }: { className?: string }) {
  return (
    <div className={`hidden lg:flex items-center justify-center flex-1 ${className}`}>
      <div className="h-0.5 w-full bg-purple-300 relative">
        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-purple-600 rounded-full" />
      </div>
    </div>
  );
}

export function DashboardMockup({ className = '' }: { className?: string }) {
  return (
    <div className={`p-4 md:p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="h-6 w-40 bg-foreground/10 rounded mb-2" />
          <div className="h-4 w-24 bg-muted-foreground/20 rounded" />
        </div>
        <div className="flex gap-2">
          <div className="h-8 w-24 bg-primary/20 rounded-lg" />
          <div className="h-8 w-8 bg-muted/50 rounded-lg" />
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Active Positions', value: '47', color: 'text-foreground' },
          { label: 'Near Limit', value: '3', color: 'text-yellow-500' },
          { label: 'At Risk', value: '1', color: 'text-red-500' },
          { label: 'Compliant', value: '43', color: 'text-green-500' },
        ].map((stat, i) => (
          <div key={i} className="rounded-lg border bg-card p-3">
            <div className="text-xs text-muted-foreground mb-1">{stat.label}</div>
            <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
          </div>
        ))}
      </div>

      {/* Position Bars */}
      <div className="rounded-lg border bg-card p-4 mb-4">
        <div className="text-sm font-medium mb-4">Position Utilization</div>
        <div className="space-y-3">
          {[
            { name: 'Corn (ZC)', pct: 85, color: 'bg-yellow-500' },
            { name: 'Wheat (ZW)', pct: 45, color: 'bg-green-500' },
            { name: 'Soybeans (ZS)', pct: 92, color: 'bg-red-500' },
            { name: 'Crude Oil (CL)', pct: 65, color: 'bg-green-500' },
          ].map((pos, i) => (
            <div key={i}>
              <div className="flex justify-between text-xs mb-1">
                <span>{pos.name}</span>
                <span className="font-medium">{pos.pct}%</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div className={`h-full ${pos.color} rounded-full`} style={{ width: `${pos.pct}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Alerts Panel */}
      <div className="rounded-lg border bg-card p-4">
        <div className="text-sm font-medium mb-3">Recent Alerts</div>
        <div className="space-y-2">
          {[
            { type: 'warning', text: 'Soybeans (ZS) at 92% limit' },
            { type: 'warning', text: 'Corn (ZC) approaching 85%' },
            { type: 'info', text: 'Daily report generated' },
          ].map((alert, i) => (
            <div key={i} className={`flex items-center gap-2 text-xs p-2 rounded ${
              alert.type === 'warning' ? 'bg-yellow-500/10 text-yellow-600' : 'bg-primary/10 text-primary'
            }`}>
              <div className={`w-1.5 h-1.5 rounded-full ${
                alert.type === 'warning' ? 'bg-yellow-500' : 'bg-primary'
              }`} />
              {alert.text}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function CheckIcon({ className = '' }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

export function XIcon({ className = '' }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
